import { definitions, Promotion } from '@luxuryescapes/contract-svc-promo'
import config from 'constants/config'

export function getItemsSpecificDetails(bv: App.Checkout.PriceBreakdownView, state: App.State): Array<definitions['Discount Request Item']> {
  return bv.items.map(item => {
    if (!item.taxesAndFees) {
      item.taxesAndFees = 0
    }

    let itemPrice = item?.price + item.taxesAndFees

    if (!itemPrice || itemPrice === 0 || isNaN(itemPrice)) {
      console.warn(`Item price is 0 for ${item.itemType} ${item.itemId} ${item.offerId}. This is not expected and this item will not contribute or be considered for promo calculations`)
      itemPrice = 0
    }

    switch (item.itemType) {
      case 'accommodation':
        return {
          itemId: item.itemId,
          categoryBK: 'hotel',
          discountableTotal: itemPrice,
          offerId: bv.items[0].offerId,
          travellers: state.checkout.form.travellerForms.map((traveller) => ({
            firstName: traveller.firstName,
            lastName: traveller.lastName,
          })),
        }
      case 'flight':
        return {
          offerId: item.offerId,
          itemId: item.itemId,
          categoryBK: 'flight',
          discountableTotal: itemPrice,
          travellers: [],
        }
      case 'experience':
      case 'transfer':
        return {
          offerId: item.offerId,
          itemId: item.itemId,
          categoryBK: 'experience',
          discountableTotal: itemPrice,
        }
      // QQQ: could consider addons as something here?
      case 'tour':
      case 'tourV2':
        return {
          offerId: item.offerId,
          itemId: item.itemId,
          categoryBK: 'tour',
          discountableTotal: itemPrice,
        }
      case 'insurance':
        return {
          offerId: item.offerId,
          itemId: item.itemId,
          categoryBK: 'insurance',
          discountableTotal: itemPrice,
        }
      case 'cruise':
        return {
          offerId: item.offerId,
          itemId: item.itemId,
          categoryBK: 'cruise',
          discountableTotal: itemPrice,
        }
      default:
        return {
          offerId: item.offerId,
          itemId: item.itemId,
          categoryBK: 'hotel',
          discountableTotal: itemPrice,
        }
    }
  })
}

/**
 * The goal of checkoutStateToDiscountOrder is to provide item level information
 * for promo calculators for all item types in a way that is consistent with the placed order
 * Next steps: we'd like to move away from the getPromoCodeProductType() mapping
 * towards making this determination, in svc-promo via the offerId (avoiding this mapping)
 * @param state
 * @param breakdownView
 * @returns
 */
export function checkoutStateToDiscountOrder(
  state: App.State
  , breakdownView: App.Checkout.PriceBreakdownView[],
): definitions['Discount Request Order'] {
  const items = breakdownView.map((bv) => {
    // Prefer the more specific price or ids on the item
    return getItemsSpecificDetails(bv, state)
  })
  const results: definitions['Discount Request Order'] = {
    region: state.geo.currentRegionCode as definitions['Promo Regions'],
    brand: config.BRAND as definitions['Promo Brands'],
    isGiftOrder: state.checkout.cart.isGift ?? false,
    items: items.flat(),
    hasBedbankPromotion: false,
    deviceType: 'web',
  }

  return results
}

export function promotionMap(promo: Promotion.Promotion): App.Promotion {
  return ({
    id: promo.id_promo_code,
    code: promo.code_name,
    discount: promo.promo_value,
    type: promo.promo_type,
    warning: promo.warning,
    maxDiscount: promo.max_discount,
    expiresAt: promo.expires_at,
    items: [],
    currency: promo.currency,
    minSpend: promo.min_spend,
    allowedPaymentMethods: promo.allowed_payment_methods,
    allowedAirlineCarriers: promo.allowed_airline_carriers,
    isCorporate: promo.is_corporate,
  })
}

export function itemPromotionMap(item: definitions['Discount Response']['promo']['items'][0], promoV2:definitions['Discount Response']['promo']):App.ItemDiscount {
  return ({
    discountAmount: item.discountAmount,
    discountType: promoV2.promo_type,
    discountableTotal: item.discountableTotal,
    maxDiscount: item.maxDiscount,
    discountValue: item.discountValue,
    offerId: item.offerId,
    categoryBK: item.categoryBK,
  })
}

export function promotionMapV2(promoV2: definitions['Discount Response']['promo']): App.Promotion {
  return ({
    id: promoV2.id_promo_code,
    code: promoV2.code_name,
    warning: promoV2.warning,
    discount: promoV2.discount,
    allowedPaymentMethods: promoV2.allowed_payment_methods,
    currency: promoV2.currency,
    maxDiscount: promoV2.max_discount,
    discountTotal: promoV2.discount_total,
    items: promoV2.items.map((i) => itemPromotionMap(i, promoV2)),
    expiresAt: promoV2.expires_at,
    minSpend: promoV2.min_spend,
    type: promoV2.type,
    corporateName: promoV2.corporate_name,
    isCorporate: promoV2.is_corporate,
  })
}

export function discountItemMap(item:definitions['Discount Response Item']): App.ItemDiscount {
  return ({
    offerId: item.offerId,
    categoryBK: item.categoryBK.toString(),
    discountableTotal: item.discountableTotal,
    maxDiscount: item.maxDiscount,
    discountValue: item.discountValue,
    discountType: item.discountType,
    discountAmount: item.discountAmount,
  })
}
